import { useSyncExternalStore } from 'react'

/** Этапы игрового режима (docs/GAME.md, «Маршруты»). */
export type PlayStage = 'setup' | 'run' | 'result'

export type Route =
  | { name: 'games' }
  | { name: 'room'; code: string }
  | { name: 'login' }
  | { name: 'projects' }
  | { name: 'editor'; projectId: string }
  | { name: 'play'; projectId: string; stage: PlayStage }

function parseStage(value: string | undefined): PlayStage {
  if (value === 'run' || value === 'result') return value
  return 'setup'
}

function parse(hash: string): Route {
  const clean = hash.replace(/^#\/?/, '')
  const parts = clean.split('/').filter(Boolean)
  if (parts.length >= 2 && parts[0] === 'projects') {
    const projectId = decodeURIComponent(parts[1] as string)
    if (parts[2] === 'play') return { name: 'play', projectId, stage: parseStage(parts[3]) }
    return { name: 'editor', projectId }
  }
  if (parts[0] === 'projects') return { name: 'projects' }
  // Короткая ссылка комнаты — то, чем делятся с друзьями.
  if (parts[0] === 'r' && parts[1]) return { name: 'room', code: decodeURIComponent(parts[1]).toUpperCase() }
  if (parts[0] === 'login') return { name: 'login' }
  return { name: 'games' }
}

/** Сравнение по значению: снимок для useSyncExternalStore обязан быть стабильным. */
function sameRoute(a: Route, b: Route): boolean {
  if (a.name !== b.name) return false
  if (a.name === 'games' || a.name === 'login' || a.name === 'projects') return true
  if (a.name === 'room' && b.name === 'room') return a.code === b.code
  if (a.name === 'editor' && b.name === 'editor') return a.projectId === b.projectId
  if (a.name === 'play' && b.name === 'play') {
    return a.projectId === b.projectId && a.stage === b.stage
  }
  return false
}

function hashFor(route: Route): string {
  if (route.name === 'editor') return `#/projects/${encodeURIComponent(route.projectId)}`
  if (route.name === 'play') {
    const base = `#/projects/${encodeURIComponent(route.projectId)}/play`
    return route.stage === 'setup' ? base : `${base}/${route.stage}`
  }
  if (route.name === 'projects') return '#/projects'
  if (route.name === 'room') return `#/r/${encodeURIComponent(route.code)}`
  if (route.name === 'login') return '#/login'
  return '#/'
}

let current: Route = typeof location === 'undefined' ? { name: 'games' } : parse(location.hash)
const listeners = new Set<() => void>()

function handleHashChange(): void {
  const next = parse(location.hash)
  if (sameRoute(next, current)) return
  current = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener('hashchange', handleHashChange)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('hashchange', handleHashChange)
  }
}

function getSnapshot(): Route {
  return current
}

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function navigate(route: Route): void {
  const hash = hashFor(route)
  if (location.hash === hash) return
  location.hash = hash
}
